import { useAssetComponents } from "@/hooks/useAssetComponents";
import { cn } from "@/lib/utils";
import { setSelectedElementKey } from "@/store/iframeSlice";
import {
  AI_MODEL,
  AI_AGENT,
  useAiActionStore,
  SEARCH_SUPPORTED_MODELS,
} from "@/store/zustand/aiActionStore";
import {
  useAiContextStore,
  useIsChatLoading,
} from "@/store/zustand/aiContextStore";
import {
  useCanvasStore,
  useMultiselectedNavTreeNode,
  useSelectedNavTreeNode,
} from "@/components/Canvas/stores/canvasStore";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import useLayoutStore, { LeftTab } from "@/store/zustand/layoutStore";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { useMCPServers } from "@/hooks/useMCPServers";
import { ACTION_TYPES } from "@/utils/ai/utils";
import { STORYBOARD_TYPES } from "@/utils/constants";
import { useLoadCodebase } from "@/utils/devServerUtils";
import { track } from "@/utils/events/tracker";
import {
  getEditorLanguageForFilename,
  getUnexpiredToken,
  useActiveCanvas,
  useCanvasId,
} from "@/utils/utils";
import * as Lucide from "lucide-react";
import { ArrowUp, ImageUp, X, Coins } from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Mention, MentionsInput } from "react-mentions";
import { useDispatch, useSelector } from "react-redux";
import { useTypedSelector } from "@/store/store";
import { toast } from "sonner";
import { useImageUploader } from "@/components/AppHeader/FileUpload";
import SimpleMonacoEditor from "../CodeEditors/SimpleMonacoEditor";
import { Button } from "../ui/button";
import ContextButton from "./components/ContextButton";
import { SelectedIssuesContext } from "./components/SelectedIssuesContext";
import { PRDVisualContext } from "./PRD/PRD";
import MCPStoreModal from "../MCP/MCPStoreModal";

import { setFigmaContext } from "@/store/supabaseSlice";
import { SuggestionsButton } from "./chat/SuggestionsButton";
import UsageIndicator from "./chat/UsageIndicator";
import ClaudeCodeSetupIndicator from "./chat/ClaudeCodeSetupIndicator";
import { useClaudeCodeAuthStore } from "@/store/zustand/claudeCodeAuthStore";
import { useUsageLimits } from "@/hooks/pricing/useUsageLimits";

import ActionDropdown from "./components/ActionDropDown";

import FeatureToggleMenu from "./components/FeatureToggleMenu";
import ActiveFeatureTags from "./components/ActiveFeatureTags";

import Icon, { ICONS } from "../Core/Icons";
import AiModelDropdown from "./components/AiModelDropDown";
import DragHoverTargets from "./components/DragHoverTargets";
import { useGlobalDragState } from "@/hooks/useDragState";

import { UploadedFile } from "@/utils/types";
import { useIframeStore } from "@/components/Canvas/stores/iframeStore";
import { FeaturePRD } from "@/types/issues";
import { useIssuesStore } from "@/store/zustand/issues-store";
import axios from "axios";
import { useSession } from "@clerk/clerk-react";
import { Loader2 } from "lucide-react";
import { constructFigmaMessageForClipboard } from "@/utils/figmaUtils";
import { TempoElement } from "@/utils/tempoElement";
import { useSubscriptionInfo } from "@/hooks/pricing/useSubscriptionInfo";

interface Props {
  addContextMode?: boolean;

  // If in chat, then it is the stand-in for the chat prompt
  isForChat?: boolean;

  // Optional agent override - when provided, uses this instead of store value
  agent?: AI_AGENT;
}

const GenerateInput: React.FC<Props> = React.memo(
  ({ addContextMode, isForChat, agent: agentProp }) => {
    const { selectedNavTreeNode, selectedElement, storyboardIdSelected } =
      useSelectedNavTreeNode();
    const { multiselectedElements, multiselectedStoryboardIds } =
      useMultiselectedNavTreeNode();

    const canvasId = useCanvasId();
    const supabaseClient = useSupabaseStore((state) => state.supabaseClient);
    const { session } = useSession();
    const { authStatus, fetchAuthStatus } = useClaudeCodeAuthStore();
    const [isCCSetupModalOpen, setIsCCSetupModalOpen] = useState(false);
    const { openModal: openMCPModal, servers: mcpServers } = useMCPServers();

    const connectedMCPCount = mcpServers.filter(
      (s) => s.connection_status === "connected" && s.enabled,
    ).length;
    const mcpTooltipText =
      connectedMCPCount > 0
        ? `${connectedMCPCount} MCP${connectedMCPCount === 1 ? "" : "s"} connected`
        : "MCP Store";

    const refreshAuth = useCallback(() => {
      if (canvasId && session) {
        fetchAuthStatus(canvasId, () =>
          session.getToken({ template: "tempo-claude-code" }),
        );
      }
    }, [canvasId, session, fetchAuthStatus]);

    const treeElementLookup = useCodebaseStore(
      (state) => state.treeElementLookup,
    );

    const figmaContext = useSelector(
      (state: any) => state.supabaseStore.figmaContext,
    );
    const { subscriptionTier } = useSubscriptionInfo();

    useEffect(() => {
      if (!figmaContext?.id || !supabaseClient) return;

      // delete any unused figma context for this canvas
      const deleteUnusedFigmaContexts = async () => {
        await supabaseClient
          .from("figma_ai_contexts")
          .delete()
          .eq("canvas_id", canvasId)
          .neq("id", figmaContext.id);
      };

      deleteUnusedFigmaContexts();
    }, [figmaContext, supabaseClient, canvasId]);
    // Canvas from DB
    const activeCanvas = useActiveCanvas();
    const {
      uploadFiles,
      uploadedFiles,
      clearUploadedFiles,
      FilePreviewerComponent,
      FileUploadComponent,
      FileUploadToCodebaseComponent,
      handleImagePaste,
      handlePastedFilesAsContext,
      handlePastedFilesToCodebase,
      isContextUploadSupported,
      pastedFiles,
      setPastedFiles,
    } = useImageUploader();

    const actionType = useAiActionStore((state) => state.actionType);

    const isChatLoading = useIsChatLoading();

    const devServerStatus = useTypedSelector(
      (state) => state.codebase.devServerStatus,
    );
    const devServerReady = devServerStatus?.isServerRunning === true;

    // Fetch auth status when dependencies change (only when devServer is ready)
    useEffect(() => {
      if (canvasId && session && devServerReady) {
        fetchAuthStatus(canvasId, () =>
          session.getToken({ template: "tempo-claude-code" }),
        );
      }
    }, [canvasId, session, devServerReady, fetchAuthStatus]);

    const dispatch = useDispatch<any>();

    const chatPromptInput = useAiActionStore((state) => state.chatPromptInput);
    const canvasPromptInput = useAiActionStore(
      (state) => state.canvasPromptInput,
    );

    const promptInput = isForChat ? chatPromptInput : canvasPromptInput;
    const setPromptInput = useCallback(
      (prompt: string) => {
        useAiActionStore
          .getState()
          .setPromptInput(isForChat ? "chat" : "canvas", prompt);
      },
      [isForChat],
    );

    const { isDragging, resetDragState } = useGlobalDragState();
    const promptPromiseRef = useRef(null);

    const featurePrd = useIssuesStore(
      (state) => state.canvasIdToIssue[canvasId]?.featurePrd,
    );

    const featurePrdVisualContext =
      featurePrd?.canvas_visual_context as any as PRDVisualContext;

    const loadCodebase = useLoadCodebase();

    const handleUploadToCodebase = useCallback(
      async (files: File[]) => {
        try {
          await uploadFiles(files, {
            forCodebase: true,
            canvasId,
            codebaseDirectory: undefined,
          });
        } catch (error: any) {
          // uploadFiles will handle the error
        } finally {
          resetDragState();
        }
      },
      [uploadFiles, canvasId, resetDragState],
    );

    enum ReferenceObjectType {
      Storyboard,
      LucideIcon,
      Component,
    }

    interface ReferenceObject {
      id: string;
      type: ReferenceObjectType;
      display: string;
    }

    const references: ReferenceObject[] = useMemo(() => {
      const storyboards: ReferenceObject[] = (activeCanvas?.storyboards || [])
        .filter((storyboard: any) => storyboard.deleted !== true)
        .filter(
          (storyboard: any) => storyboard.type !== STORYBOARD_TYPES.APPLICATION,
        )
        .map((storyboard: any) => {
          return {
            id: `storyboard-${storyboard.id}`,
            type: ReferenceObjectType.Storyboard,
            display: storyboard.name,
          };
        });

      const components = Object.values(treeElementLookup)
        .filter((treeElement: any) => {
          return (
            treeElement.type === "component" ||
            treeElement.type === "storybook-component"
          );
        })
        .map((treeElement: any) => {
          return {
            id: `component-${treeElement.codebaseId}`,
            type: ReferenceObjectType.Component,
            display: treeElement.componentName,
          };
        });

      const icons: ReferenceObject[] = Object.keys(Lucide).map((iconName) => {
        return {
          id: `lucide-icon-${iconName}`,
          type: ReferenceObjectType.LucideIcon,
          display: iconName,
          icon: Lucide[iconName],
        };
      });

      return [...storyboards, ...components, ...icons];
    }, [
      activeCanvas,
      treeElementLookup,
      ReferenceObjectType.Component,
      ReferenceObjectType.LucideIcon,
      ReferenceObjectType.Storyboard,
    ]);

    const queryIcons = useCallback(
      (query, callback) => {
        if (query.length <= 2) return;

        const matches = references
          .filter(
            (reference) => reference.type === ReferenceObjectType.LucideIcon,
          )
          .filter((reference) => {
            return (
              reference.display.toLowerCase().indexOf(query.toLowerCase()) > -1
            );
          });
        return matches;
      },
      [references, ReferenceObjectType.LucideIcon],
    );

    const canvasComponentKey = useCanvasStore(
      (state) => state.canvasComponentKey,
    );

    const { componentKeyToStories } = useAssetComponents();

    const setNewLayout = useLayoutStore((state) => state.setNewLayout);

    const inComponentsView = useMemo(() => {
      return !!canvasComponentKey;
    }, [canvasComponentKey]);

    const useReasoning = useAiContextStore((state) => state.useReasoning);
    const setUseReasoning = useAiContextStore((state) => state.setUseReasoning);

    const useSearch = useAiContextStore((state) => state.useSearch);

    const chatOnlyMode = useAiContextStore((state) => state.chatOnlyMode);
    const setChatOnlyMode = useAiContextStore((state) => state.setChatOnlyMode);

    const { remainingUsage } = useUsageLimits();

    const codeGenerationModel = useAiActionStore(
      (state) => state.codeGenerationModel,
    );
    const setCodeGenerationModel = useAiActionStore(
      (state) => state.setCodeGenerationModel,
    );
    const agentFromStore = useAiActionStore((state) => state.agent);
    // Use prop if provided, otherwise fall back to store value
    const agent = agentProp ?? agentFromStore;
    const isClaudeAgentSelected = agent === AI_AGENT.CLAUDE_AGENT;

    // Automatically disable search when switching to unsupported models
    useEffect(() => {
      if (useSearch && !SEARCH_SUPPORTED_MODELS.has(codeGenerationModel)) {
        useAiContextStore.getState().setUseSearch(false);
      }
    }, [codeGenerationModel, useSearch]);

    // Automatically switch to Gemini Flash in chat-only mode
    useEffect(() => {
      if (chatOnlyMode) {
        setCodeGenerationModel(AI_MODEL.GEMINI_2_0_FLASH);
      }
    }, [chatOnlyMode, setCodeGenerationModel]);

    useEffect(() => {
      if (SEARCH_SUPPORTED_MODELS.has(codeGenerationModel) && useSearch) {
        useAiContextStore.getState().setUseSearch(true);
      }
    }, [codeGenerationModel, useSearch]);

    useEffect(() => {
      // The one for chat does not affect this
      if (isForChat) {
        return;
      }

      if (addContextMode) {
        useAiActionStore.getState().setActionType(ACTION_TYPES.ADD_CONTEXT);
        return;
      }

      if (selectedElement.isEmpty()) {
        useAiActionStore.getState().setActionType(ACTION_TYPES.GENERATE);
      } else {
        useAiActionStore.getState().setActionType(ACTION_TYPES.EDIT);
      }
    }, [addContextMode, selectedElement, isForChat]);

    useEffect(() => {
      // The one for chat does not affect this
      if (isForChat) {
        return;
      }

      if (addContextMode) {
        useAiActionStore.getState().setActionType(ACTION_TYPES.ADD_CONTEXT);
        return;
      }

      if (!selectedElement.isEmpty()) {
        useAiActionStore.getState().setActionType(ACTION_TYPES.EDIT);
      }
    }, [selectedElement, addContextMode, isForChat]);

    useEffect(() => {
      // The one for chat does not affect this
      if (isForChat) {
        return;
      }

      // This will make sure that, Whenever the user clicks on the canvas, when the edit mode is active then the action type will be set to generate
      if (actionType === ACTION_TYPES.EDIT && selectedElement.isEmpty()) {
        useAiActionStore.getState().setActionType(ACTION_TYPES.GENERATE);
      }
    }, [selectedElement, actionType, isForChat]);

    const sendEditAIRequest = async (
      prompt: string,
      filesToAttach: UploadedFile[],
    ) => {
      if (isChatLoading) {
        toast.info("There's already a prompt in progress!");
        return;
      }

      if (!prompt?.trim()) {
        return;
      }

      // User selection
      const elements = multiselectedElements?.length
        ? multiselectedElements
        : [selectedElement];

      const treeElements = !selectedElement.isStoryboard()
        ? elements.map((element) => treeElementLookup[element.codebaseId])
        : [];
      const storyboardIds = storyboardIdSelected
        ? [storyboardIdSelected]
        : (multiselectedStoryboardIds ?? []);

      const storyboards =
        activeCanvas?.storyboards?.filter((sb) =>
          storyboardIds.includes(sb.id),
        ) ?? [];

      const totalUserSelection = treeElements
        .concat(storyboards as any[])
        .filter((element) => element);

      if (!totalUserSelection?.length) {
        toast.error("Selected element not found in codebase, refreshing.");
        // If there is an error it could be because the codebase is out of sync, automatically reload the codebase
        // Mark this as flushing even though it's technically not to show the syncing indicator and to prevent more changes
        await loadCodebase();
        return;
      }

      const sent = useAiContextStore.getState().setSendChatMessage({
        sendChatMessage: `Please apply this change to the items I selected: ${prompt}`,
        sendChatMessageFiles: filesToAttach,
      });

      if (sent) {
        setNewLayout(LeftTab.AIChat);
        track("AI_PROMPT_REQUESTED", {
          type: "AI_CHAT_IN_CANVAS_EDIT",
          prompt,
          filesToAttach,
        });
        setPromptInput("");
        clearUploadedFiles();
      }
    };

    const sendGenerateNewVariantRequest = async (
      prompt: string,
      filesToAttach: UploadedFile[],
    ) => {
      if (!prompt?.trim()) {
        return;
      }

      if (isChatLoading) {
        return;
      }

      // toast.warning("Not implemented");

      // @ts-expect-error TS2538
      const stories = componentKeyToStories[canvasComponentKey] || [];
      const relativeFilename =
        stories[0]?.filename.split(canvasId + "/")[1] || stories[0]?.filename;

      const injectedPrompt = `
I am looking at various stories for a React component. Help me generate a new variant in the stories file for this component.
You should use .stories.tsx file and the component itself for context.

Task: ${prompt}
      
The stories I am looking at are in the file ${relativeFilename}.
    `;

      const sent = useAiContextStore.getState().setSendChatMessage({
        sendChatMessage: injectedPrompt,
        sendChatMessageFiles: filesToAttach,
      });

      if (sent) {
        setNewLayout(LeftTab.AIChat);
        track("AI_PROMPT_REQUESTED", {
          type: "AI_CHAT_IN_CANVAS_GENERATE_VARIANT",
          prompt,
          filesToAttach,
        });
        setPromptInput("");
        clearUploadedFiles();
      }
    };

    const sendGenerateAIRequest = async (
      prompt: string,
      filesToAttach: UploadedFile[],
    ) => {
      if (actionType !== ACTION_TYPES.GENERATE) {
        console.error("Invalid action type");
        throw new Error("Invalid action type");
      }

      if (isChatLoading) {
        return;
      }

      if (!prompt || prompt.trim() === "") {
        return;
      }

      // @ts-expect-error TS2322
      promptPromiseRef.current = new AbortController();

      const sent = useAiContextStore.getState().setSendChatMessage({
        sendChatMessage: `Please create a brand new component for me and create a new Storyboard to showcase it using this prompt: ${prompt}`,
        sendChatMessageFiles: filesToAttach,
      });
      if (sent) {
        setNewLayout(LeftTab.AIChat);
        track("AI_PROMPT_REQUESTED", {
          type: "AI_CHAT_IN_CANVAS_GENERATE",
          prompt,
          filesToAttach,
        });
        setPromptInput("");
        clearUploadedFiles();
      }
    };

    const [inputMounted, setInputMounted] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
      const inputElement = inputRef.current;
      if (inputElement) {
        // Apply height constraints to the internal textarea
        if (!remainingUsage.hasPromptsLeft) {
          // @ts-expect-error TS2339
          inputElement.style.maxHeight = "200px";
        }
        // @ts-expect-error TS2339
        inputElement.style.overflowY = "auto";
        // @ts-expect-error TS2339
        inputElement.style.resize = "none";
      }

      // @ts-expect-error TS2339
      inputRef.current?.focus();
    }, [inputMounted, remainingUsage.hasPromptsLeft]);

    useEffect(() => {
      // When the chat starts loading, reset the selection
      if (isChatLoading) {
        dispatch(setSelectedElementKey(null));
      }
    }, [isChatLoading, dispatch]);

    const [isFigmaContextLoading, setIsFigmaContextLoading] = useState(false);

    useEffect(() => {
      if (
        promptInput.includes("<figma-context-id>") &&
        !isFigmaContextLoading
      ) {
        setIsFigmaContextLoading(true);

        const figmaContextId = promptInput.match(
          /<figma-context-id>(.*?)<\/figma-context-id>/,
        )?.[1];

        if (figmaContextId) {
          getUnexpiredToken(session).then((supabaseJWT: string) => {
            axios
              .get(
                `${
                  import.meta.env.VITE_BACKEND_BASE_URL
                }/figma/context/${figmaContextId}?delete=false`,
                {
                  headers: {
                    Authorization: `Bearer ${supabaseJWT}`,
                  },
                },
              )
              .then(async (res) => {
                const figmaContext = res.data;
                if (!figmaContext || Object.keys(figmaContext).length === 0) {
                  console.error("Figma context not found");
                  return;
                }
                dispatch(setFigmaContext(figmaContext));
              })
              .catch((err) => {
                console.error(err);
              })
              .finally(() => {
                setPromptInput(
                  promptInput.replace(
                    /<figma-context-id>.*?<\/figma-context-id>/g,
                    "",
                  ),
                );
                setIsFigmaContextLoading(false);
              });
          });
        }
      }
    }, [promptInput, session, dispatch, setPromptInput, isFigmaContextLoading]);

    const handleSubmit = useCallback(async () => {
      if (!featurePrd || !supabaseClient) {
        return;
      }

      if (!promptInput || promptInput.trim() === "") {
        return;
      }

      if (promptInput.length > MAX_INPUT_LENGTH) {
        toast.warning("Please shorten your prompt to under 50,000 characters");
        return;
      }

      if (isChatLoading) {
        return;
      }

      const filesToAttach = uploadedFiles;

      // Just send as a regular chat message
      const allFiles = [...filesToAttach].filter(Boolean);

      if (figmaContext?.image_url) {
        allFiles.push({
          url: figmaContext?.image_url,
          name: "figma_image",
          type: "image",
          mimeType: "image/png",
        });
      }

      if (isForChat) {
        let figmaMessage = "";
        if (figmaContext && Object.keys(figmaContext).length > 0) {
          const { initial_code, figma_context, frame_images } = figmaContext;

          let figmaImages = "";
          let figmaIcons = "";
          try {
            const frameImagesJson = JSON.parse(frame_images);

            for (const image of frameImagesJson.images) {
              figmaImages += `\n<image_${image.nodeId}>\n${image.url}\n</image_${image.nodeId}>\n`;
            }

            for (const icon of frameImagesJson.icons) {
              figmaIcons += `\n<icon_${icon.nodeId}>\n${icon.svg}\n</icon_${icon.nodeId}>\n`;
            }
          } catch (e) {
            console.error(e);
            toast.error("Error parsing Figma data, please try again.");
            return;
          }

          figmaMessage = constructFigmaMessageForClipboard(
            initial_code,
            figma_context,
            figmaImages,
            figmaIcons,
          );
        }
        const sent = useAiContextStore.getState().setSendChatMessage({
          sendChatMessage: `${promptInput}${
            figmaContext && Object.keys(figmaContext).length > 0
              ? `\n<figma_input>\n${figmaMessage}\n</figma_input>\n`
              : ""
          }`,
          // @ts-expect-error TS2322
          sendChatMessageFiles: allFiles?.length ? allFiles : null,
        });

        if (sent) {
          setPromptInput("");
          clearUploadedFiles();
          // Only track AI_CHAT for non-Claude Agent sessions
          // Claude Agent tracks it from useSendClaudeCodeMessage
          if (!isClaudeAgentSelected) {
            track("AI_PROMPT_REQUESTED", {
              type: "AI_CHAT",
              prompt: promptInput,
              filesToAttach,
            });
          }
        }
        return;
      }

      if (actionType === ACTION_TYPES.ADD_CONTEXT) {
        let context;
        if (selectedElement.isStoryboard()) {
          context = promptInput;
        } else {
          const selectedTreeElement =
            treeElementLookup[selectedElement.codebaseId];
          if (!selectedTreeElement) {
            console.error("Could not find selected tree element");
            toast.error("Could not find selected element in codebase");
            return;
          }

          context = `Element ${selectedTreeElement.jsxName} on line ${
            selectedTreeElement.startLine
          } in file ${
            selectedTreeElement.filename.split(canvasId + "/")[1]
          } - desired action: "${promptInput}"`;
        }

        const newContext = [
          ...(featurePrdVisualContext || []),
          {
            text: context,
            file_urls: filesToAttach,
          },
        ];

        try {
          useIssuesStore.getState().updateFeaturePRD(supabaseClient, {
            id: featurePrd.id,
            canvas_id: canvasId,
            canvas_visual_context: newContext,
          });

          useLayoutStore.getState().setAddContextMode(false);

          setPromptInput("");
          dispatch(setSelectedElementKey(null));
        } catch (e) {
          console.error(e);
          toast.error("Could not add context to EngSpec");
          return;
        }

        return;
      }

      if (actionType === ACTION_TYPES.EDIT) {
        sendEditAIRequest(promptInput, filesToAttach);
      } else if (inComponentsView) {
        sendGenerateNewVariantRequest(promptInput, filesToAttach);
      } else {
        sendGenerateAIRequest(promptInput, filesToAttach);
      }
    }, [
      featurePrd,
      supabaseClient,
      promptInput,
      isChatLoading,
      uploadedFiles,
      figmaContext,
      isForChat,
      actionType,
      inComponentsView,
      setPromptInput,
      clearUploadedFiles,
      selectedElement,
      featurePrdVisualContext,
      treeElementLookup,
      canvasId,
      dispatch,
      sendEditAIRequest,
      sendGenerateNewVariantRequest,
      sendGenerateAIRequest,
    ]);

    const MAX_INPUT_LENGTH = 50000;

    const handleInput = useCallback(
      (event) => {
        if (isChatLoading) {
          return;
        }

        const inputValue = event.target.value.trimStart();
        setPromptInput(inputValue);
      },
      [isChatLoading, setPromptInput],
    );

    const clearContext = () => {
      dispatch(setSelectedElementKey(null));
    };

    const clearFigmaContext = async () => {
      if (!supabaseClient) {
        return;
      }

      await supabaseClient
        .from("figma_ai_contexts")
        .delete()
        .eq("canvas_id", canvasId);
      dispatch(setFigmaContext(null));
    };

    const streamingTokenCount = useAiActionStore(
      (state) => state.streamingTokenCount,
    );

    const actionText = useMemo(() => {
      if (isChatLoading) {
        if (streamingTokenCount > 0) {
          const tokenDisplay =
            streamingTokenCount >= 1000
              ? `${(streamingTokenCount / 1000).toFixed(1)}k`
              : streamingTokenCount.toString();
          return `Replying... (${tokenDisplay} tokens)`;
        }
        return "Replying...";
      }

      if (isForChat) {
        return "Ask anything";
      }

      if (isDragging) {
        return "Upload an image to use as context";
      }

      if (pastedFiles.length > 0) {
        return "Choose where to upload your pasted files";
      }

      if (inComponentsView) {
        return "Generate a blue variant of this button";
      }

      if (actionType === ACTION_TYPES.GENERATE) {
        return "Create a chat interface";
      } else if (actionType === ACTION_TYPES.EDIT) {
        if (selectedElement.isEmpty()) {
          return "Select an element on the canvas to edit";
        } else {
          return "Change the color of this button to blue";
        }
      } else if (actionType === ACTION_TYPES.ADD_CONTEXT) {
        if (selectedElement.isEmpty()) {
          return "Select an element to add context for";
        } else {
          return "Change the color to blue";
        }
      }
    }, [
      actionType,
      inComponentsView,
      selectedElement,
      isDragging,
      isChatLoading,
      streamingTokenCount,
      isForChat,
      pastedFiles?.length,
    ]);

    // Handle keyboard shortcuts for clearing pasted files
    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (pastedFiles.length > 0) {
          // Only handle if no input is focused to avoid interfering with typing
          const activeElement = document.activeElement;
          const isInputFocused =
            activeElement &&
            (activeElement.tagName === "INPUT" ||
              activeElement.tagName === "TEXTAREA" ||
              (activeElement as HTMLElement).contentEditable === "true");

          if (!isInputFocused) {
            if (
              e.key === "Escape" ||
              (e.key === "z" && (e.ctrlKey || e.metaKey))
            ) {
              e.preventDefault();
              e.stopPropagation();
              setPastedFiles([]);
            }
          }
        }
      };

      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }, [pastedFiles.length, setPastedFiles]);

    const container = useRef(null);

    const showAddedContextButton =
      (actionType === ACTION_TYPES.EDIT ||
        actionType === ACTION_TYPES.ADD_CONTEXT ||
        isForChat) &&
      !selectedElement.isEmpty();

    const showFigmaContextButton =
      (figmaContext && Object.keys(figmaContext).length > 0) ||
      isFigmaContextLoading;
    const addedContextButton = showAddedContextButton ? (
      <ContextButton
        element={selectedElement}
        // @ts-expect-error TS2322
        navTreeNode={selectedNavTreeNode}
        onClick={() => {
          clearContext();
          if (!addContextMode) {
            useAiActionStore.getState().setActionType(ACTION_TYPES.GENERATE);
          }
        }}
        storyboardSelected={Boolean(storyboardIdSelected)}
      />
    ) : null;

    const figmaContextButton = showFigmaContextButton ? (
      !isFigmaContextLoading ? (
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "h-4 text-primary-n-500 bg-[#6448EF33] p-2 py-3 flex gap-1 text-3xs font-medium mb-0.5 cursor-pointer relative",
            "hover:text-primary-n-500 hover:bg-[#6448EF33]",
          )}
          onClick={clearFigmaContext}
        >
          <img src="/images/figma-logo.svg" alt="Figma" className="h-3 w-3" />
          <span>{figmaContext?.component_name}</span>
          <X className="stroke-primary-n-500 h-3 w-3" />
        </Button>
      ) : (
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "h-4 text-primary-n-500 bg-[#6448EF33] p-2 py-3 flex items-center gap-2 text-3xs font-medium mb-0.5 cursor-default",
            "hover:text-primary-n-500 hover:bg-[#6448EF33]",
          )}
        >
          <div className="flex items-center gap-1.5">
            <img src="/images/figma-logo.svg" alt="Figma" className="h-3 w-3" />
            <Loader2 className="w-3 h-3 animate-spin text-primary-0" />
          </div>
        </Button>
      )
    ) : null;

    const cleanPromptInput = (promptInput: string) => {
      let cleanedPrompt = promptInput;

      // hide in chat when pasted
      if (cleanedPrompt.includes("<figma-context-id>")) {
        cleanedPrompt = cleanedPrompt.replace(
          /<figma-context-id>.*?<\/figma-context-id>/g,
          "",
        );
      }
      return cleanedPrompt;
    };

    const canGenerateOrAddContext =
      actionType === ACTION_TYPES.GENERATE ||
      actionType === ACTION_TYPES.ADD_CONTEXT;

    // Function to determine if the FileUploadComponent should be shown
    const shouldShowFileUpload = () => {
      const canEdit =
        actionType === ACTION_TYPES.EDIT && !selectedElement.isEmpty();
      const canShowFileUpload =
        !isDragging && (remainingUsage.hasPromptsLeft || isClaudeAgentSelected);

      return (canGenerateOrAddContext || canEdit) && canShowFileUpload;
    };

    return (
      <>
        <MCPStoreModal />
        <div
          className="flex w-full flex-col max-h-[65vh] min-h-0"
          data-generate-input="true"
        >
          <div className="primary rounded-lg bg-background-transparent h-full flex flex-col min-h-0">
            {isForChat && (
              <div className="flex flex-col gap-1 pt-1 shrink-0">
                <div className="flex items-center justify-between w-full">
                  {(authStatus !== "valid" || isCCSetupModalOpen) &&
                  subscriptionTier !== "free" ? (
                    <ClaudeCodeSetupIndicator
                      isForChat={isForChat}
                      authStatus={authStatus}
                      onCCModalStateChange={setIsCCSetupModalOpen}
                      onAuthRefresh={refreshAuth}
                    />
                  ) : (
                    <UsageIndicator isForChat={isForChat} />
                  )}
                  <SuggestionsButton
                    onSelectSuggestion={(suggestion) => {
                      const suggestionBody = `${suggestion.short_title}\n\n${suggestion.detailed_description}\n\nMake this change for me in the codebase if you can`;
                      setPromptInput(suggestionBody);
                    }}
                  />
                </div>
              </div>
            )}

            <div
              className={cn(
                "flex flex-col p-1.5 bg-background-primary w-full border rounded-lg shadow-lg pointer-events-auto relative flex-1 min-h-0 overflow-visible",
                isForChat && "border rounded-lg",
                isForChat && "focus-within:border-primary-0",
              )}
              onPaste={handleImagePaste}
            >
              {actionType !== ACTION_TYPES.ADD_CONTEXT &&
                actionType !== ACTION_TYPES.EDIT &&
                promptInput.length > MAX_INPUT_LENGTH * 0.8 && (
                  <div
                    className={cn(
                      // 8px above the submit
                      "absolute top-2 right-2 z-10 text-2xs px-2 py-1 rounded-md border border-red-400",
                      promptInput.length >= MAX_INPUT_LENGTH
                        ? "text-red-400 bg-red-950/80"
                        : "text-yellow-400 bg-yellow-950/80",
                    )}
                  >
                    {promptInput.length}/{MAX_INPUT_LENGTH}
                  </div>
                )}
              {(actionType === ACTION_TYPES.EDIT ||
                actionType === ACTION_TYPES.ADD_CONTEXT) &&
              selectedElement.isEmpty() ? (
                <div>
                  <div className="relative p-2 border border-dashed border-secondary-n-700 rounded-lg">
                    <X
                      data-tooltip-content={"Exit Pick & Edit"}
                      data-tooltip-id="tooltip"
                      data-tooltip-place="bottom"
                      onClick={() => {
                        if (actionType === ACTION_TYPES.EDIT) {
                          clearContext();
                          useAiActionStore
                            .getState()
                            .setActionType(ACTION_TYPES.GENERATE);
                        }
                      }}
                      className="w-4 h-4 text-secondary-n-600 absolute right-1 top-1 cursor-pointer"
                    />
                    <div className="h-full flex flex-col items-center gap-2 justify-center max-w-48 mx-auto">
                      <Lucide.SquareMousePointer
                        data-tooltip-id="tooltip"
                        data-tooltip-place="bottom"
                        className="w-4 h-4 text-secondary-n-600"
                      />
                      <p className="text-xs text-secondary-n-50 text-center">
                        Select a storyboard or element to give AI more context.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col h-full min-h-0">
                  {/* Scrollable content area */}
                  <div className="flex-1 overflow-y-auto min-h-0">
                    <div
                      className={cn(
                        "flex flex-col items-start justify-start gap-2 pl-2",
                        (showAddedContextButton && isForChat) ||
                          uploadedFiles.length > 0
                          ? "py-1"
                          : null,
                      )}
                    >
                      <FilePreviewerComponent />
                      {isForChat && showAddedContextButton
                        ? addedContextButton
                        : null}
                      {showFigmaContextButton ? figmaContextButton : null}
                      {agent === AI_AGENT.PM_AGENT && <SelectedIssuesContext />}
                    </div>
                    <div
                      className={cn(
                        "flex w-full flex-row justify-center items-end text-2xs text-white leading-none relative",
                      )}
                    >
                      <div ref={container} />
                      <div
                        className={cn(
                          "flex items-end justify-center transition-all duration-200 ease-in-out w-full max-w-full gap-0.5",
                        )}
                      >
                        <div
                          className={cn(
                            "flex-1 flex items-center gap-0.5 min-w-0",
                            !isForChat && "min-w-40 ml-0",
                          )}
                        >
                          {isDragging || pastedFiles.length > 0 ? (
                            <div className="w-full relative">
                              <DragHoverTargets
                                pastedFiles={pastedFiles}
                                isContextDisabled={
                                  pastedFiles.length > 0 &&
                                  !isContextUploadSupported(pastedFiles)
                                }
                                onUploadFiles={(files) => {
                                  if (pastedFiles.length > 0) {
                                    handlePastedFilesAsContext(pastedFiles);
                                  } else {
                                    uploadFiles(files);
                                    resetDragState();
                                  }
                                }}
                                onUploadToCodebase={(files) => {
                                  if (pastedFiles.length > 0) {
                                    handlePastedFilesToCodebase(pastedFiles);
                                  } else {
                                    handleUploadToCodebase(files);
                                  }
                                }}
                              />
                            </div>
                          ) : (
                            <div className="flex-1 min-w-0 overflow-hidden relative">
                              {!remainingUsage.hasPromptsLeft &&
                                !isClaudeAgentSelected && (
                                  <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                                    <div className="flex flex-col items-center gap-2">
                                      <Coins className="w-5 h-5 text-secondary-n-500" />
                                      <p className="text-xs text-center max-w-xs">
                                        You're out of prompts. Upgrade to keep
                                        using AI, or continue editing manually.
                                      </p>
                                    </div>
                                  </div>
                                )}
                              <MentionsInput
                                data-testid="chat-input"
                                className={cn(
                                  "mentions-input min-h-7 mx-1 mb-4 focus:outline-hidden outline-hidden bg-transparent text-xs font-medium w-full",
                                  !isForChat && "max-w-[18rem] ml-0",
                                  isForChat && "flex-1",
                                )}
                                style={{
                                  paddingRight: "8px",
                                  height:
                                    remainingUsage.hasPromptsLeft ||
                                    isClaudeAgentSelected
                                      ? "auto"
                                      : "4rem",
                                  overflowY: "auto",
                                  resize: "none",
                                  minHeight: "1.75rem",
                                }}
                                value={cleanPromptInput(promptInput)}
                                inputRef={(ref) => {
                                  inputRef.current = ref;
                                  setInputMounted(true);
                                }}
                                onChange={handleInput}
                                placeholder={
                                  remainingUsage.hasPromptsLeft ||
                                  isClaudeAgentSelected
                                    ? actionText
                                    : ""
                                }
                                disabled={
                                  (actionType !== ACTION_TYPES.GENERATE &&
                                    selectedElement.isEmpty()) ||
                                  isChatLoading ||
                                  (!remainingUsage.hasPromptsLeft &&
                                    !isClaudeAgentSelected)
                                }
                                rows={1}
                                onKeyDown={(e: React.KeyboardEvent) => {
                                  if (
                                    (e.key === "Enter" || e.keyCode === 13) &&
                                    !e.shiftKey
                                  ) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleSubmit();
                                  }
                                }}
                                // Renders the SuggestionList above the cursor if there is not enough space below
                                allowSuggestionsAboveCursor
                                suggestionsPortalHost={container.current}
                                onPaste={handleImagePaste}
                              >
                                <Mention
                                  data={references.filter(
                                    (reference) =>
                                      reference.type ===
                                        ReferenceObjectType.Storyboard ||
                                      reference.type ===
                                        ReferenceObjectType.Component,
                                  )}
                                  displayTransform={(id, display) => {
                                    return `@${display}`;
                                  }}
                                  trigger="@"
                                  renderSuggestion={(
                                    suggestion,
                                    search,
                                    highlightedDisplay,
                                  ) => (
                                    <div className="flex h-max">
                                      {highlightedDisplay}
                                    </div>
                                  )}
                                  markup="@[__display__](__id__)"
                                  className="bg-primary-200"
                                  appendSpaceOnAdd
                                />
                                <Mention
                                  data={queryIcons}
                                  displayTransform={(id, display) => {
                                    return `\<${display} \/\>`;
                                  }}
                                  markup=":[__display__](__id__)"
                                  trigger=":"
                                  renderSuggestion={(
                                    suggestion,
                                    search,
                                    highlightedDisplay,
                                  ) => {
                                    const Icon = Lucide[suggestion.display];
                                    return (
                                      <div className="flex flex-row justify-between items-center space-x-1">
                                        <span>{highlightedDisplay}</span>
                                        {Icon && <Icon className="w-4 h-4" />}
                                      </div>
                                    );
                                  }}
                                  className="bg-primary-200"
                                  appendSpaceOnAdd
                                />
                              </MentionsInput>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    className={cn(
                      "bg-background-primary rounded-md flex gap-1 p-1 items-center shrink-0 min-w-0 overflow-hidden",
                      isDragging && "hidden",
                    )}
                  >
                    <ActionDropdown
                      shouldShowFileUpload={shouldShowFileUpload()}
                      FileUploadComponent={FileUploadComponent}
                      FileUploadToCodebaseComponent={
                        FileUploadToCodebaseComponent
                      }
                      handleAddContext={() => {
                        if (addContextMode) {
                          clearContext();
                          return;
                        }

                        if (actionType === ACTION_TYPES.EDIT) {
                          clearContext();
                          useAiActionStore
                            .getState()
                            .setActionType(ACTION_TYPES.GENERATE);
                        } else {
                          clearContext();
                          useAiActionStore
                            .getState()
                            .setActionType(ACTION_TYPES.EDIT);
                        }
                      }}
                      value={codeGenerationModel}
                      onChange={(value: AI_MODEL) => {
                        setCodeGenerationModel(value);
                      }}
                    />
                    {!isClaudeAgentSelected && (
                      <FeatureToggleMenu
                        isChatLoading={isChatLoading}
                        useReasoning={useReasoning}
                        setUseReasoning={setUseReasoning}
                        useSearch={useSearch}
                        useChatOnly={chatOnlyMode}
                        setUseChatOnly={setChatOnlyMode}
                        codeGenerationModel={codeGenerationModel}
                      />
                    )}
                    {isForChat && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={openMCPModal}
                        className="h-7 w-7 p-1 rounded-md transition-colors bg-secondary-n-950 shrink-0"
                        data-tooltip-id="tooltip"
                        data-tooltip-content={mcpTooltipText}
                        data-tooltip-place="top"
                      >
                        <img
                          src="/icons/mcp.svg"
                          alt="MCP"
                          className="h-4 w-4 opacity-60"
                        />
                      </Button>
                    )}
                    <div>
                      <AiModelDropdown
                        codeGenerationModel={codeGenerationModel}
                      />
                    </div>
                    <ActiveFeatureTags
                      useReasoning={useReasoning}
                      useSearch={useSearch}
                      useChatOnly={chatOnlyMode}
                      isChatLoading={isChatLoading}
                      codeGenerationModel={codeGenerationModel}
                    />

                    <div className="flex items-center justify-end gap-0.5 space-x-0.5 ml-auto shrink-0">
                      {(remainingUsage.hasPromptsLeft ||
                        isClaudeAgentSelected) && (
                        <div
                          className={cn(
                            "transition-all duration-200 pl-1 shrink-0 flex items-center",
                            (actionType === ACTION_TYPES.EDIT ||
                              actionType === ACTION_TYPES.ADD_CONTEXT) &&
                              selectedElement.isEmpty() &&
                              "w-0 hidden cursor-not-allowed pointer-events-none",
                          )}
                        >
                          {isChatLoading ? (
                            <Button
                              variant="primary"
                              onClick={() =>
                                useAiActionStore.getState().setStopAiTrigger()
                              }
                              className="h-7 w-7 p-1 rounded-md shrink-0"
                            >
                              <Icon
                                icon={ICONS.CIRCLE_STOP}
                                className="h-4 w-4 text-secondary-900"
                              />
                            </Button>
                          ) : (
                            <Button
                              data-testid="chat-send-button"
                              variant="primary"
                              disabled={
                                !promptInput.trim() ||
                                promptInput.length > MAX_INPUT_LENGTH ||
                                (isClaudeAgentSelected &&
                                  authStatus !== "valid")
                              }
                              className="h-7 w-7 p-1 rounded-md shrink-0"
                              onClick={handleSubmit}
                            >
                              <ArrowUp
                                data-tooltip-content="Submit"
                                data-tooltip-id="tooltip"
                                data-tooltip-place="bottom"
                                className={
                                  "size-4 text-secondary-900 cursor-pointer hover:text-white focus:outline-hidden"
                                }
                              />
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    );
  },
);
GenerateInput.displayName = "GenerateInput";

export default GenerateInput;
